/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { CalendarDate, isSameDay, maxDate, minDate } from '@internationalized/date';
import assign from 'lodash/assign';
import cloneDeep from 'lodash/cloneDeep';
import filter from 'lodash/filter';
import find from 'lodash/find';
import findKey from 'lodash/findKey';
import forEach from 'lodash/forEach';
import get from 'lodash/get';
import has from 'lodash/has';
import includes from 'lodash/includes';
import indexOf from 'lodash/indexOf';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import isString from 'lodash/isString';
import keys from 'lodash/keys';
import mapValues from 'lodash/mapValues';
import merge from 'lodash/merge';
import minBy from 'lodash/minBy';
import reduce from 'lodash/reduce';
import set from 'lodash/set';
import size from 'lodash/size';
import some from 'lodash/some';
import toInteger from 'lodash/toInteger';
import type { DateValue } from 'react-aria';
import {
    calendarDateToUtcString,
    localNow,
    localToday,
    utcStringToCalendarDate,
} from 'sarsaparilla';

import type {
    AvailabilityCampsite,
    Campground,
    CampsiteAvailabilities,
    NightMap,
    Reservation,
    Selection,
    Selections,
} from '../../../types/camping';
import { CampsiteAvailableTypes } from '../../../types/camping';
import { TYPE_OF_USE_DAY } from '../constants';
import {
    CAMPSITE_AVAILABLE,
    CAMPSITE_CHECKOUT_ONLY,
    CAMPSITE_CLOSED,
    CAMPSITE_EARLY_ACCESS,
    CAMPSITE_EARLY_ACCESS_AVAILABLE,
    CAMPSITE_LOTTERY,
    CAMPSITE_LOTTERY_OPEN,
    CAMPSITE_NOT_AVAILABLE,
    CAMPSITE_NOT_RESERVABLE,
    CAMPSITE_NOT_YET_RELEASED,
    CAMPSITE_OPEN,
    CAMPSITE_RESERVED,
    CAMPSITE_RESERVED_AVAILABLE,
    CAMPSITE_UNAVAILABLE,
    POTENTIALLY_RESERVABLE_STATUSES,
    RESERVABLE_AVAILABILITY_STATUSES,
} from '../constants/availability';
import { ITINERARY_TYPE_FACILITY, VOYAGEURS_FACILITY_ID } from '../constants/campground';
import { ITINERARY_GROUPED } from '../constants/itinerary';
import {
    checkInFlightFacilityTimeReservationStartDate,
    formatAvailabilityAccessor,
    getDatesFromRange,
    getDaysDifference,
    isDateSameOrAfter,
    UTCStringToCalendarDate,
    UTCStringToZonedDateTime,
} from './dateUtils';
import { filterActiveEarlyAccessLotteries } from './earlyAccessLotteryUtils';
import { isValidUUID } from './utils';

const isDayAvailableNew = ({
    status,
    isSelectedDate,
    isInternal,
}: {
    status: CampsiteAvailableTypes;
    isSelectedDate: boolean;
    isInternal: boolean;
}) => {
    const isReservableWalkup = isInternal && includes(status, CAMPSITE_NOT_RESERVABLE);
    const isReservableLottery = isInternal && includes(status, CAMPSITE_LOTTERY);
    const isReservableCheckout = isSelectedDate && status === CAMPSITE_CHECKOUT_ONLY;

    const isAvailable =
        status === CAMPSITE_AVAILABLE || // 'Available'
        status === CAMPSITE_EARLY_ACCESS_AVAILABLE || // Early access winner
        status === CAMPSITE_RESERVED_AVAILABLE || // if day is reserved by the current reservation
        isReservableWalkup || // if booking as PMO from the HUB, can be 'Not Reservable' (a.k.a FCFS)
        isReservableCheckout || // if day is the selected date, it can be 'CHECKOUT'
        isReservableLottery; // if booking as PMO from the HUB, can be 'Lottery'

    return isAvailable;
};

const isRangeValid = ({
    availabilities,
    rangeStart,
    rangeEnd,
    isInternal = false,
}: {
    availabilities: CampsiteAvailabilities;
    rangeStart: CalendarDate;
    rangeEnd: CalendarDate;
    isInternal: boolean;
}) => {
    let isAvailable = true;
    let date = rangeStart;

    while (date.compare(rangeEnd) < 0) {
        const accessor = formatAvailabilityAccessor(date);
        const isCurDateAvailable = isDayAvailableNew({
            status: availabilities[accessor],
            isSelectedDate: isSameDay(date, rangeStart),
            isInternal,
        });

        if (!isCurDateAvailable) {
            isAvailable = false;
            break;
        }
        date = date.add({ days: 1 });
    }
    return isAvailable;
};

const findClosestStartDate = (date: DateValue, dateRangeMap: any) => {
    const diffMap: any[] = [];
    forEach(dateRangeMap, (end, start) => {
        const diff = getDaysDifference(calendarDateToUtcString(date) as string, start);
        diffMap.push({ date: start, diff });
    });

    const nearestDateObj = minBy(diffMap, (o) => o.diff);

    return nearestDateObj.date;
};

export const isDayAvailable = ({
    status,
    selectedDate,
    matchedSelection,
    extendedRange,
    isTransfer,
    previousStatus,
    isInternal,
}: {
    status: CampsiteAvailableTypes;
    selectedDate: DateValue;
    matchedSelection: Selection;
    extendedRange: DateValue;
    isTransfer?: boolean;
    previousStatus: CampsiteAvailableTypes;
    isInternal: boolean;
}) => {
    const isItinerary = isString(matchedSelection);
    const isReservableLottery = isInternal && includes(status, CAMPSITE_LOTTERY);
    const isReservableWalkup = isInternal && includes(status, CAMPSITE_NOT_RESERVABLE);
    const isPrevReservableWalkup =
        isInternal && includes(previousStatus, CAMPSITE_NOT_RESERVABLE);
    const isReservableOpen = isInternal && includes(status, CAMPSITE_OPEN);

    // (1) check if previous date is available (applicable when previous date is a checkout-only date)
    const isPrevDayAvailable =
        previousStatus === CAMPSITE_AVAILABLE ||
        previousStatus === CAMPSITE_EARLY_ACCESS_AVAILABLE ||
        previousStatus === CAMPSITE_RESERVED_AVAILABLE ||
        previousStatus === CAMPSITE_OPEN ||
        previousStatus === CAMPSITE_LOTTERY_OPEN ||
        isPrevReservableWalkup ||
        isReservableLottery ||
        (isItinerary &&
            (status === CAMPSITE_AVAILABLE ||
                status === CAMPSITE_EARLY_ACCESS_AVAILABLE)); // for itinerary facility, if selected date is AVAILABLE, we can assume previous date is available

    // if date is reserved (has status of [reservation_id]) and previous date is available, this is a reservable checkout date
    const isReservableCheckout = isValidUUID(status) && isPrevDayAvailable;

    /* (2) if date is
        - 'Available'
        - 'Reserved Available'
        - 'Open'
        - or reservable 'Not Reservable'
        - or reservable checkout-only date
    then date can be selected */
    const isAvailable =
        status === CAMPSITE_AVAILABLE ||
        status === CAMPSITE_EARLY_ACCESS_AVAILABLE ||
        (previousStatus === CAMPSITE_EARLY_ACCESS_AVAILABLE &&
            status === CAMPSITE_OPEN) ||
        (previousStatus === CAMPSITE_RESERVED_AVAILABLE && status === CAMPSITE_OPEN) ||
        status === CAMPSITE_RESERVED_AVAILABLE ||
        status === CAMPSITE_CHECKOUT_ONLY ||
        status === CAMPSITE_LOTTERY_OPEN ||
        isReservableWalkup ||
        isReservableLottery ||
        isReservableOpen ||
        isReservableCheckout;

    if (extendedRange) {
        if (
            isTransfer ||
            selectedDate.compare(UTCStringToCalendarDate(matchedSelection.startDate)) > 0
        ) {
            const isCheckoutOpenDate =
                (isEqual(status, CAMPSITE_AVAILABLE) ||
                    isEqual(status, CAMPSITE_EARLY_ACCESS_AVAILABLE)) &&
                isEqual(previousStatus, CAMPSITE_OPEN);
            const isAvailableOpenDate =
                isEqual(status, CAMPSITE_OPEN) && isEqual(previousStatus, CAMPSITE_OPEN);
            const isFirstOpenDate =
                isEqual(status, CAMPSITE_OPEN) &&
                (isEqual(previousStatus, CAMPSITE_AVAILABLE) ||
                    isEqual(previousStatus, CAMPSITE_EARLY_ACCESS_AVAILABLE));
            // if date is OPEN or is checkout-only OPEN date, current date is officially available
            if (isCheckoutOpenDate || isAvailableOpenDate || isFirstOpenDate) {
                return true;
            }
        }
    }
    // if current and previous date are both available, current date is selectable
    return isAvailable && isPrevDayAvailable;
};

const extendExistingSelection = ({
    selectedDate,
    matchedSelection,
    availabilities,
    canHaveMultiSelectionsPerSite,
    isTransfer,
    isInternal = false,
}: {
    selectedDate: DateValue;
    matchedSelection: Selection;
    availabilities: CampsiteAvailabilities;
    canHaveMultiSelectionsPerSite: boolean;
    isTransfer?: boolean;
    isInternal: boolean;
}) => {
    const finalSelection = cloneDeep(matchedSelection);
    let shouldUpdate = false;

    let available = true;
    const extendedRange = selectedDate.add({ weeks: 2 });
    const isExtendingAfterEnd =
        selectedDate.compare(UTCStringToCalendarDate(matchedSelection.endDate)) >= 0;

    // --- EXTEND Forward ---
    if (isExtendingAfterEnd) {
        for (
            let j = UTCStringToCalendarDate(matchedSelection.endDate);
            j <= selectedDate;
            j = j.add({ days: 1 })
        ) {
            const accessor = formatAvailabilityAccessor(j);
            const prevAccessor = formatAvailabilityAccessor(j.subtract({ days: 1 }));
            const isAvailable = isDayAvailable({
                status: availabilities[accessor],
                selectedDate,
                matchedSelection,
                extendedRange,
                isTransfer,
                previousStatus: availabilities[prevAccessor],
                isInternal,
            });

            if (
                !isAvailable ||
                isEqual(availabilities[prevAccessor], CAMPSITE_RESERVED) ||
                isValidUUID(availabilities[prevAccessor])
            ) {
                available = false;
                break;
            }
        }

        if (available) {
            const updatedEndDate = selectedDate.add({ days: 1 });

            finalSelection.endDate = calendarDateToUtcString(updatedEndDate) as string;
            finalSelection.actualEndDate = calendarDateToUtcString(
                selectedDate
            ) as string;
            shouldUpdate = true;

            // If multiple range selections in a site is allowed, update dateRangeMap
            if (canHaveMultiSelectionsPerSite) {
                const dateRangeMap = cloneDeep(matchedSelection.dateRangeMap) || {};

                const matchStartDate = findClosestStartDate(updatedEndDate, dateRangeMap);
                // update end date in matched start/end entry
                dateRangeMap[matchStartDate] = calendarDateToUtcString(
                    updatedEndDate
                ) as string;
                finalSelection.dateRangeMap = dateRangeMap;
            }
        }
    }
    // --- EXTEND Backward---
    else {
        available = isRangeValid({
            availabilities,
            rangeStart: selectedDate as CalendarDate,
            rangeEnd: UTCStringToCalendarDate(matchedSelection.startDate),
            isInternal,
        });

        if (available) {
            shouldUpdate = true;
            const isExtendingBeforeStart =
                selectedDate.compare(
                    UTCStringToCalendarDate(matchedSelection.startDate)
                ) < 0;

            const dateRangeMap = cloneDeep(matchedSelection.dateRangeMap) || {};

            // Extend before previous start date for STANDARD selection
            if (!canHaveMultiSelectionsPerSite) {
                const updatedStartDate = selectedDate;
                finalSelection.startDate = calendarDateToUtcString(
                    updatedStartDate
                ) as string;
            }
            // Extend before previous start date for ITINERARY_SINGLE selection
            else if (canHaveMultiSelectionsPerSite && isExtendingBeforeStart) {
                finalSelection.startDate = calendarDateToUtcString(
                    selectedDate
                ) as string;
                const oldStartDate = matchedSelection.startDate;

                // Preserve the extension range's end date, then delete the outdated start/end entry
                const preservedEndDate = dateRangeMap[oldStartDate];
                delete dateRangeMap[oldStartDate];

                // Map new start date to preserved end date
                dateRangeMap[calendarDateToUtcString(selectedDate) as string] =
                    preservedEndDate;
            }
            // Extending between campsite ITINERARY_SINGLE selection's start and end dates
            else {
                dateRangeMap[matchedSelection.startDate] = calendarDateToUtcString(
                    selectedDate.add({ days: 1 })
                ) as string;
            }
            finalSelection.dateRangeMap = dateRangeMap;
        }
    }
    return { finalSelection, shouldUpdate };
};

export const allDaysAreAvailable = ({
    availabilities,
    startDate,
    endDate,
    isInternal = false,
}: {
    availabilities: CampsiteAvailabilities;
    startDate: CalendarDate;
    endDate: CalendarDate;
    isInternal: boolean;
}) => {
    // Check if all dates between tomorrow and the newly selected end date are available
    for (let j = startDate; j.compare(endDate) < 0; j = j.add({ days: 1 })) {
        const accessor = formatAvailabilityAccessor(j);

        const isAvailable = isDayAvailableNew({
            status: availabilities[accessor],
            isSelectedDate: isSameDay(j, startDate),
            isInternal,
        });

        if (!isAvailable) {
            return false;
        }
    }

    return true;
};

const updateWithTransferSelection = ({
    selectedDate,
    selectedCampsiteId,
    availabilities,
    reservedStartDate,
    isInternal = false,
}: {
    selectedDate: DateValue;
    selectedCampsiteId: string;
    availabilities: CampsiteAvailabilities;
    reservedStartDate: CalendarDate | null;
    isInternal?: boolean;
}) => {
    const finalSelection: Selection = {
        startDate: '',
        endDate: '',
        actualEndDate: '',
        name: '',
        loop: '',
        campsiteId: '',
        dateRangeMap: undefined,
    };
    let shouldUpdate = false;

    const days = reservedStartDate && isSameDay(selectedDate, reservedStartDate) ? 2 : 1;
    const selectedEndDate = selectedDate.add({ days });
    const selectedActualEndDate = selectedDate.add({ days: days - 1 });
    // Check if all dates between tomorrow and the newly selected end date are available
    const available = allDaysAreAvailable({
        availabilities,
        startDate: localToday.add({ days: 1 }),
        endDate: selectedEndDate as CalendarDate,
        isInternal,
    });
    // If at least one date is not available, or newly selected end date is before the current start date (or today), selection is invalid
    if (
        !available ||
        (reservedStartDate && selectedEndDate.compare(reservedStartDate) < 0) ||
        selectedEndDate.compare(localToday) < 0
    ) {
        shouldUpdate = false;
    }
    // Else when all future dates are available, new end date and campsite selection are valid
    else {
        finalSelection.startDate = calendarDateToUtcString(reservedStartDate) as string;
        finalSelection.endDate = calendarDateToUtcString(selectedEndDate) as string;
        finalSelection.actualEndDate = calendarDateToUtcString(
            selectedActualEndDate
        ) as string;
        finalSelection.campsiteId = selectedCampsiteId;
        shouldUpdate = true;
    }
    return { finalSelection, shouldUpdate };
};

const campsiteDateToRemove = (matchedSelection: Selection, selectedDate: DateValue) => {
    if (isEmpty(matchedSelection)) {
        return null;
    }

    const matchedSelectionEndDate = matchedSelection?.actualEndDate;

    // If there is a dateRangeMap, find if there is a sub-range to delete
    if (matchedSelection.dateRangeMap) {
        const startDate = findKey(matchedSelection.dateRangeMap, (end, start) => {
            return (
                selectedDate.compare(UTCStringToCalendarDate(start)) >= 0 &&
                selectedDate.compare(UTCStringToCalendarDate(end)) < 0
            );
        });
        if (
            matchedSelectionEndDate &&
            isSameDay(selectedDate, UTCStringToCalendarDate(matchedSelectionEndDate))
        ) {
            return {
                campsiteId: matchedSelection.campsiteId,
                startDate,
            };
        }

        return null;
    }

    const selectedStartDate = matchedSelection.startDate;

    if (matchedSelectionEndDate) {
        if (isSameDay(UTCStringToCalendarDate(matchedSelectionEndDate), selectedDate)) {
            return {
                campsiteId: matchedSelection.campsiteId,
                startDate: selectedStartDate,
            };
        }
    }
    return null;
};

const updateSelectionsNew = ({
    selectedDate,
    selectedCampsiteId,
    campgroundDateRange,
    isItinerarySingle,
    availabilities,
    isInternal = false,
}: {
    selectedDate: DateValue;
    selectedCampsiteId: string;
    campgroundDateRange?: any;
    isItinerarySingle: boolean;
    availabilities: CampsiteAvailabilities;
    isInternal: boolean;
}) => {
    const startDate = selectedDate;
    const endDate = selectedDate.add({ days: 2 });
    const actualEndDate = selectedDate.add({ days: 1 });
    const loop = get(campgroundDateRange, `${selectedCampsiteId}.loop`, null);
    const name = get(campgroundDateRange, `${selectedCampsiteId}.site`, null);
    const finalSelection: Selection = {
        startDate: calendarDateToUtcString(startDate) as string,
        endDate: calendarDateToUtcString(endDate) as string,
        actualEndDate: calendarDateToUtcString(actualEndDate) as string,
        campsiteId: selectedCampsiteId,
        loop,
        name,
    };

    const available = allDaysAreAvailable({
        availabilities,
        startDate: startDate as CalendarDate,
        endDate: actualEndDate as CalendarDate,
        isInternal,
    });

    // can't select a start date being checkout only
    const isDateSelectedCheckOutOnly =
        availabilities[formatAvailabilityAccessor(selectedDate as CalendarDate)] ===
        CampsiteAvailableTypes.CAMPSITE_CHECKOUT_ONLY;

    if (!available || isDateSelectedCheckOutOnly) {
        return {
            shouldUpdate: false,
            finalSelection,
        };
    }

    // Add dateMap if the facility is type ITINERARY_SINGLE
    if (isItinerarySingle) {
        finalSelection.dateRangeMap = {
            [calendarDateToUtcString(startDate) as string]: calendarDateToUtcString(
                endDate
            ) as string,
        };
    }

    return { finalSelection, shouldUpdate: true };
};

export const updateStandardSelections = ({
    selections,
    newDate,
    newCampsiteId,
    availabilitiesData,
    reservation,
    externalReservation,
    isTransfer,
    isInternal,
    canModifyInflightReservation,
    name,
    loop,
}: {
    selections: Selections;
    newDate: DateValue;
    newCampsiteId: string;
    availabilitiesData: Record<string, any>;
    reservation: Reservation;
    externalReservation: Reservation;
    isTransfer: boolean;
    isInternal: boolean;
    canModifyInflightReservation: boolean;
    name: string;
    loop: string;
}) => {
    const reservedStartDate = reservation
        ? utcStringToCalendarDate(reservation.start_date)
        : null;

    const { availabilities, type_of_use: typeOfUse } = availabilitiesData;

    const isDayUse = typeOfUse === TYPE_OF_USE_DAY;

    const newSelections = cloneDeep(selections);
    const selection = newSelections[newCampsiteId];
    const isRemoval = campsiteDateToRemove(selection, newDate);

    // Remove
    if (isRemoval) {
        delete newSelections[newCampsiteId];
    }
    // Day Use
    else if (isDayUse) {
        const newStartDate = calendarDateToUtcString(newDate);
        set(newSelections, newCampsiteId, {
            startDate: newStartDate,
            endDate: newStartDate,
            actualEndDate: newStartDate,
            campsiteId: newCampsiteId,
            campsiteName: name,
            campsiteLoop: loop,
        });
    }
    // Extension
    else if (selections[newCampsiteId]) {
        const { finalSelection, shouldUpdate } = extendExistingSelection({
            selectedDate: newDate,
            matchedSelection: selection,
            availabilities,
            canHaveMultiSelectionsPerSite: false,
            isTransfer,
            isInternal,
        });

        if (shouldUpdate) {
            set(newSelections, newCampsiteId, finalSelection);
        }
    }
    // Transfer
    else if (!selections[newCampsiteId] && isTransfer) {
        const { finalSelection, shouldUpdate } = updateWithTransferSelection({
            selectedDate: newDate,
            selectedCampsiteId: newCampsiteId,
            availabilities,
            reservedStartDate,
            isInternal,
        });

        if (shouldUpdate) {
            set(newSelections, newCampsiteId, finalSelection);
        }
    }
    // In flight modification
    else if (!selections[newCampsiteId] && canModifyInflightReservation) {
        const externalReservedStartDate = externalReservation
            ? utcStringToCalendarDate(externalReservation.start_date)
            : null;
        const { finalSelection, shouldUpdate } = updateWithTransferSelection({
            selectedDate: newDate,
            selectedCampsiteId: newCampsiteId,
            availabilities,
            reservedStartDate: externalReservedStartDate,
        });

        if (shouldUpdate) {
            set(newSelections, newCampsiteId, finalSelection);
        }
    }

    // New selection
    else {
        const { finalSelection, shouldUpdate } = updateSelectionsNew({
            selectedDate: newDate,
            selectedCampsiteId: newCampsiteId,
            isItinerarySingle: false,
            availabilities,
            isInternal,
        });

        set(finalSelection, 'name', name);
        set(finalSelection, 'loop', loop);

        if (shouldUpdate) {
            set(newSelections, newCampsiteId, finalSelection);
        }
    }

    return newSelections;
};

const isGroupSizeInBounds = ({
    groupSizeFilter,
    campsite,
}: {
    groupSizeFilter: number;
    campsite: any;
}) => {
    let isInBound = true;
    if (groupSizeFilter) {
        const minAttribute = find(campsite.attributes, {
            attribute_name: 'Min Num of People',
        });
        const maxAttribute = find(campsite.attributes, {
            attribute_name: 'Max Num of People',
        });

        const minGroupSize = toInteger(get(minAttribute, 'attribute_value', '')) || 0;
        const maxGroupSize = toInteger(get(maxAttribute, 'attribute_value', '')) || 9999;

        isInBound = groupSizeFilter >= minGroupSize && groupSizeFilter <= maxGroupSize;
    }
    return isInBound;
};

const updateCampsiteAvailabilitiesByGroupSize = ({
    groupSizeFilter,
    campsite,
    campsiteAvailabilities,
}: {
    groupSizeFilter: number;
    campsite: any;
    campsiteAvailabilities: any;
}) => {
    // (1) check if campsite group size is in bound with the group size filter value
    // if group size is in bounds, return availabilities as is
    if (isGroupSizeInBounds({ groupSizeFilter, campsite })) {
        return campsiteAvailabilities;
    }
    // else if group size is out of bounds, mark all date's availability to CAMPSITE_NOT_AVAILABLE
    const newCampsiteAvailabilities = { ...campsiteAvailabilities };
    forEach(newCampsiteAvailabilities, (value, key) => {
        newCampsiteAvailabilities[key] = CAMPSITE_NOT_AVAILABLE;
    });

    return newCampsiteAvailabilities;
};

const updateAvailabilityUponSelection = ({
    campsiteId: selectionCampsiteId,
    start: selectionStart,
    end: selectionEnd,
    loop: selectionLoop,
    campsiteAvailabilities,
    campsite,
    isGroupedItinerary,
    isItinerarySingle,
}: {
    campsiteId: string;
    start: CalendarDate;
    end: CalendarDate;
    loop: string;
    campsiteAvailabilities: any;
    campsite: any;
    isGroupedItinerary: boolean;
    isItinerarySingle: boolean;
}) => {
    if (
        isGroupedItinerary &&
        campsite.campsite_id !== selectionCampsiteId &&
        campsite.loop === selectionLoop
    ) {
        forEach(campsiteAvailabilities, (status, date) => {
            assign(campsiteAvailabilities, { [date]: CAMPSITE_UNAVAILABLE });
        });
    } else {
        /* Else if matched campsite is NOT part of ITINERARY_GROUPED (or ITINERARY_DEFAULT)... */
        // For each day in the campsite ...
        forEach(campsiteAvailabilities, (status, date) => {
            const day = UTCStringToCalendarDate(date);

            /* If campsite is part of an itinerary that allows switching sites within the sliding window,
            mark all dates after the selected end date within this campsite with a status of 'Open' as 'Available' */
            if (
                day.compare(selectionEnd) > 0 &&
                (isItinerarySingle || campsite.campsite_id === selectionCampsiteId) &&
                isEqual(campsiteAvailabilities[date], CAMPSITE_OPEN)
            ) {
                set(campsiteAvailabilities, date, CAMPSITE_AVAILABLE);
            }
            // If day of matched campsite overlaps with the selection's date range, mark day as 'Unavailable'
            else if (day.compare(selectionStart) >= 0 && day.compare(selectionEnd) <= 0) {
                set(campsiteAvailabilities, date, CAMPSITE_UNAVAILABLE);
            }
        });
    }
};

export const isGroupedItinerary = (campgroundId: string) =>
    !isEqual(indexOf(ITINERARY_GROUPED, campgroundId), -1);

const updateItineraryCampsiteAvailabilitiesBySelections = ({
    campsiteAvailabilities,
    campsite,
    selections,
    isItinerarySingle,
}: {
    campsiteAvailabilities: any;
    campsite: any;
    selections: Selections;
    isItinerarySingle: boolean;
}) => {
    const newCampsiteAvailabilities = cloneDeep(campsiteAvailabilities);

    // For each campsite selected...
    mapValues(selections, (selection) => {
        const updateProps = {
            campsiteAvailabilities: newCampsiteAvailabilities,
            campsite,
            isGroupedItinerary: isGroupedItinerary(campsite.asset_id),
        };

        forEach(selection.dateRangeMap, (e, s) => {
            /* for the current selection range, update all other campsite availabilities
            that are not selected by the current selection to 'Unavailable' */
            if (selection.campsiteId !== campsite.campsite_id) {
                updateAvailabilityUponSelection({
                    ...updateProps,
                    start: UTCStringToCalendarDate(s),
                    end: UTCStringToCalendarDate(e).subtract({ days: 2 }),
                    loop: selection.loop || '',
                    campsiteId: selection.campsiteId!,
                    isItinerarySingle,
                });
            }
        });
    });
    return newCampsiteAvailabilities;
};

export const isStatusReservable = (status: string) => {
    return includes(POTENTIALLY_RESERVABLE_STATUSES, status);
};

const findNextAvailableCheckoutOnlyDate = ({
    availabilities,
    startDate,
    canBookFCFS,
}: {
    availabilities: CampsiteAvailabilities;
    startDate: CalendarDate;
    canBookFCFS: boolean;
}) => {
    const lastDayOfAvailability = UTCStringToCalendarDate(
        Object.keys(availabilities).sort().pop()!
    );

    for (
        let day = startDate.add({ days: 1 });
        day.compare(lastDayOfAvailability) <= 0;
        day = day.add({ days: 1 })
    ) {
        const nextCheckoutOnlyDate = formatAvailabilityAccessor(day);
        const shouldChangeFCFStToCheckout =
            !canBookFCFS &&
            includes(availabilities[nextCheckoutOnlyDate], 'Not Reservable');
        const status = availabilities[nextCheckoutOnlyDate];

        if (
            !status ||
            status === CAMPSITE_RESERVED ||
            status === CAMPSITE_NOT_AVAILABLE ||
            status === CAMPSITE_UNAVAILABLE ||
            status === CAMPSITE_NOT_YET_RELEASED ||
            status === CAMPSITE_CHECKOUT_ONLY ||
            status === CAMPSITE_EARLY_ACCESS ||
            status === CAMPSITE_CLOSED ||
            isValidUUID(status) ||
            shouldChangeFCFStToCheckout
        ) {
            return nextCheckoutOnlyDate;
        }
    }
    return null;
};

const updateItineraryCampsiteAvailabilitiesWithCheckoutDate = ({
    campsiteSelection,
    campsiteAvailabilities,
    canBookFCFS,
}: {
    campsiteSelection: Selection;
    campsiteAvailabilities: any;
    canBookFCFS: boolean;
}) => {
    const newCampsiteAvailabilities = { ...campsiteAvailabilities };
    const checkoutDaysForCampsite = [];
    forEach(campsiteSelection.dateRangeMap, (end) => {
        const checkoutOnlyDate = findNextAvailableCheckoutOnlyDate({
            availabilities: campsiteAvailabilities,
            startDate: UTCStringToCalendarDate(end).subtract({ days: 1 }),
            canBookFCFS,
        });

        if (checkoutOnlyDate) {
            const prevDate = UTCStringToCalendarDate(checkoutOnlyDate).subtract({
                days: 1,
            });
            const prevDateAccessor = formatAvailabilityAccessor(prevDate);

            /* if prev date is potentially reservable in the ORIGINAL availabilities object,
            extend the 1 checkout-only date with status of "Checkout"
            but don't extend an already "Checkout" day */
            const prevDateStatus = newCampsiteAvailabilities[prevDateAccessor];
            if (
                isStatusReservable(prevDateStatus) &&
                prevDateStatus !== CampsiteAvailableTypes.CAMPSITE_CHECKOUT_ONLY
            ) {
                checkoutDaysForCampsite.push(checkoutOnlyDate);
                set(newCampsiteAvailabilities, checkoutOnlyDate, CAMPSITE_CHECKOUT_ONLY);
            }
        }
    });

    return newCampsiteAvailabilities;
};

export const updateCampsiteAvailabilitiesByQueuingLottery = ({
    campsiteAvailabilities,
    queueLotteries,
    userEntries,
    reservation,
}: {
    campsiteAvailabilities: any;
    queueLotteries: any;
    userEntries: any;
    reservation: Reservation;
}) => {
    // Set the availability based on the queuing lottery data
    const newCampsiteAvailabilities = { ...campsiteAvailabilities };
    forEach(newCampsiteAvailabilities, (value, key) => {
        const availabilityDate = UTCStringToCalendarDate(key);
        const applicableLotteries = filter(queueLotteries, (lottery) => {
            const reservationStart = UTCStringToCalendarDate(
                lottery.reservation_start
            ).subtract({ days: 1 });
            const reservationEnd = UTCStringToCalendarDate(lottery.reservation_end).add({
                days: 1,
            });

            return (
                availabilityDate.compare(reservationStart) > 0 &&
                availabilityDate.compare(reservationEnd) < 0
            );
        });

        // If the date is a checkout only date (sliding window) and a lottery applies to it replace the value as `Lottery`
        if (value === CAMPSITE_OPEN && !isEmpty(applicableLotteries)) {
            newCampsiteAvailabilities[key] = CAMPSITE_LOTTERY_OPEN;
        }

        let isWinnerInTimeSlot = false;
        let isReservationInsideEA = false;
        forEach(applicableLotteries, (lottery) => {
            if (!isWinnerInTimeSlot) {
                const entry = get(
                    userEntries,
                    `byLotteryId[${lottery.lottery_id}]`,
                    null
                );

                if (!isNil(entry)) {
                    isWinnerInTimeSlot =
                        entry.isWinner &&
                        isDateSameOrAfter(
                            localNow,
                            UTCStringToZonedDateTime(entry.assignedTimeSlot)
                        );
                }
            }

            if (reservation && !isReservationInsideEA) {
                const today = localToday;
                const earlyAccessStart = UTCStringToCalendarDate(
                    get(lottery, 'early_access_start')
                );
                const earlyAccessEnd = UTCStringToCalendarDate(
                    get(lottery, 'early_access_end')
                );
                const lotteryStartDate = UTCStringToCalendarDate(
                    get(lottery, 'reservation_start')
                );
                const lotteryEndDate = UTCStringToCalendarDate(
                    get(lottery, 'reservation_end')
                );
                const startDate = UTCStringToCalendarDate(get(reservation, 'start_date'));

                isReservationInsideEA =
                    today.compare(earlyAccessStart) >= 0 &&
                    today.compare(earlyAccessEnd) <= 0 &&
                    startDate.compare(lotteryStartDate) >= 0 &&
                    today.compare(lotteryEndDate) <= 0;
            }
        });

        // If the availability is an early access and the user is a winner in his assigned slot update the availability
        if (
            value === CAMPSITE_EARLY_ACCESS &&
            (isWinnerInTimeSlot || isReservationInsideEA)
        ) {
            // Since `Available` outside of the booking window is shown as `Not reservable` we need a new status that gets treated as
            // available but that can skip the calendar campground season dates check
            newCampsiteAvailabilities[key] = CAMPSITE_EARLY_ACCESS_AVAILABLE;
        }
    });

    return newCampsiteAvailabilities;
};

/**
 * If a campsite includes a boat, any day AVAILABLE will automatically
 * be converted to CHECKOUT ONLY, but only when the following day is RESERVED.
 */
const transformVoyaAvailabilities = ({
    campsiteAvailabilities,
}: {
    campsiteAvailabilities: CampsiteAvailabilities;
}) => {
    const sortedDays = keys(campsiteAvailabilities).sort();

    forEach(sortedDays, (day) => {
        const availabilityStatus = campsiteAvailabilities[day];

        if (availabilityStatus === CampsiteAvailableTypes.CAMPSITE_RESERVED) {
            const prevDay = UTCStringToCalendarDate(day).subtract({ days: 1 });
            const prevDayFormatted = formatAvailabilityAccessor(prevDay);
            const prevDayAvailabilityStatus = campsiteAvailabilities[prevDayFormatted];

            if (prevDayAvailabilityStatus === CampsiteAvailableTypes.CAMPSITE_AVAILABLE) {
                campsiteAvailabilities[prevDayFormatted] =
                    CampsiteAvailableTypes.CAMPSITE_CHECKOUT_ONLY;
            }
        }
    });

    return campsiteAvailabilities;
};

export const updateDataSource = ({
    campsites,
    campground,
    availabilitiesMap,
    selections,
    groupSize,
    isItinerarySingle,
    earlyAccessLotteries,
    newCampsiteId,
    canBookFCFS,
    internalOriginalReservation,
    isModifyingReservation,
    itineraryFlow,
}: {
    campsites: any;
    campground: Campground;
    availabilitiesMap: any;
    selections: Selections;
    groupSize: number;
    isItinerarySingle: boolean;
    earlyAccessLotteries: any;
    newCampsiteId: string;
    canBookFCFS: boolean;
    internalOriginalReservation: any;
    isModifyingReservation: boolean;
    itineraryFlow: boolean;
}) => {
    const updatedAvailabilities = { ...availabilitiesMap };
    // for each campsite availabilities...
    if (campsites && !isEmpty(availabilitiesMap)) {
        const hasQueuingLottery =
            isEqual(get(campground, 'facility_rules.hasQueueLottery.value', 0), 1) &&
            !isEmpty(get(campground, 'queue_lotteries', []));

        forEach(campsites, (campsite) => {
            const campsiteId = campsite.campsite_id;

            let newCampsiteAvailabilities = {
                ...get(availabilitiesMap, `${campsiteId}.availabilities`, {}),
            };

            if (availabilitiesMap[campsiteId]) {
                /* (1) update current campsite availabilities based on group size
                - all dates in the current campsite should be marked 'NOT AVAILABLE' if campsite group size cannot accommodate the group size entered */
                if (groupSize) {
                    newCampsiteAvailabilities = updateCampsiteAvailabilitiesByGroupSize({
                        groupSizeFilter: groupSize,
                        campsite,
                        campsiteAvailabilities:
                            availabilitiesMap[campsiteId].availabilities,
                    });
                }

                /* (2) if matched campsite is ITINERARY_SINGLE (does NOT allow overlapping date selections) and selections are made,
                need to process availability statuses on overlapped sites */
                if (itineraryFlow) {
                    const finalCampsite = cloneDeep(availabilitiesMap[campsiteId]);
                    /* (2a) Need to preserve the original availabilities so there's something for the column sorting
                    to work with, otherwise the rows would jump around on every click */
                    finalCampsite.sortableAvailabilities = {
                        ...newCampsiteAvailabilities,
                    };

                    /* (2) update current campsite availabilities based on selections
                    - all date ranges that are selected by another campsite should be update as 'NOT AVAILABLE' */
                    newCampsiteAvailabilities =
                        updateItineraryCampsiteAvailabilitiesBySelections({
                            campsiteAvailabilities: newCampsiteAvailabilities,
                            campsite,
                            selections,
                            isItinerarySingle,
                        });

                    if (campground.facility_id === VOYAGEURS_FACILITY_ID) {
                        const includesBoat = find(
                            get(campsite, 'attributes', []),
                            (attribute) => attribute.attribute_name === 'Boat Included'
                        );
                        if (includesBoat) {
                            newCampsiteAvailabilities = transformVoyaAvailabilities({
                                campsiteAvailabilities: newCampsiteAvailabilities,
                            });
                        }
                    }

                    /* (3) if cur campsite is selected, its campsite availabilities to find 'CHECKOUT' date(s) based on the selected date range(s)
                    only if the campsite was last selected */
                    if (
                        selections[campsiteId] &&
                        (newCampsiteId === campsiteId || isModifyingReservation)
                    ) {
                        newCampsiteAvailabilities =
                            updateItineraryCampsiteAvailabilitiesWithCheckoutDate({
                                campsiteSelection: selections[campsiteId],
                                campsiteAvailabilities: newCampsiteAvailabilities,
                                canBookFCFS,
                            });
                    }

                    finalCampsite.availabilities = newCampsiteAvailabilities;
                    updatedAvailabilities[campsiteId] = finalCampsite;
                }

                /* (3) if the campground has a queuing lottery update the availabilities based on the lottery dates */
                if (hasQueuingLottery) {
                    const queueLotteries = campground.queue_lotteries ?? {};
                    const activeLotteries = filterActiveEarlyAccessLotteries(
                        queueLotteries,
                        campground.facility_time_zone
                    );

                    if (!isEmpty(activeLotteries)) {
                        newCampsiteAvailabilities =
                            updateCampsiteAvailabilitiesByQueuingLottery({
                                queueLotteries: activeLotteries,
                                userEntries: earlyAccessLotteries,
                                campsiteAvailabilities: newCampsiteAvailabilities,
                                reservation: internalOriginalReservation,
                            });

                        const finalCampsite = cloneDeep(availabilitiesMap[campsiteId]);
                        finalCampsite.availabilities = newCampsiteAvailabilities;
                        updatedAvailabilities[campsiteId] = finalCampsite;
                    }
                }
            }
        });
    }

    return updatedAvailabilities;
};

const extendWithNewSelection = ({
    selectedDate,
    matchedSelection,
    campgroundDateRange,
    isItinerarySingle,
}: {
    selectedDate: DateValue;
    matchedSelection: Selection;
    campgroundDateRange: any;
    isItinerarySingle: boolean;
}) => {
    const isExtendingAfter =
        selectedDate.compare(UTCStringToCalendarDate(matchedSelection.endDate)) >= 0;
    const selectedCampsiteId = matchedSelection.campsiteId;

    //@ts-ignore
    let finalSelection: Selection = {};
    const shouldUpdate = true;

    const dateRangeMap = cloneDeep(matchedSelection.dateRangeMap) || {};
    dateRangeMap[calendarDateToUtcString(selectedDate) as string] =
        calendarDateToUtcString(selectedDate.add({ days: 2 })) as string;

    finalSelection = {
        dateRangeMap,
        startDate: isExtendingAfter
            ? matchedSelection.startDate
            : (calendarDateToUtcString(selectedDate) as string),
        endDate: isExtendingAfter
            ? (calendarDateToUtcString(selectedDate.add({ days: 2 })) as string)
            : matchedSelection.endDate,
        actualEndDate: isExtendingAfter
            ? (calendarDateToUtcString(selectedDate.add({ days: 1 })) as string)
            : matchedSelection.actualEndDate,
        campsiteId: selectedCampsiteId,
    };

    // If campground is ITINERARY, also store loop name (for when updating date source)
    if (isItinerarySingle) {
        finalSelection.loop = campgroundDateRange[selectedCampsiteId!].loop;
    }

    return { finalSelection, shouldUpdate };
};

const hasOtherCampsiteInRange = (
    selectedDate: DateValue,
    matchedSelection: Selection,
    selections: Selections
) => {
    const isExtendingAfter =
        selectedDate.compare(UTCStringToCalendarDate(matchedSelection.endDate)) >= 0;

    const range = isExtendingAfter
        ? getDatesFromRange(
              UTCStringToCalendarDate(matchedSelection.actualEndDate),
              selectedDate.subtract({ days: 1 })
          )
        : getDatesFromRange(
              selectedDate,
              UTCStringToCalendarDate(matchedSelection.startDate).subtract({ days: 1 })
          );

    // Returns true if there is a date in the potential selection range that matches any of the start dates in selection's dateRangeMap
    return some(range, (date) =>
        find(selections, (selection) => has(selection.dateRangeMap, date as string))
    );
};

const handleRemoveSubRange = (
    selection: Selection,
    toBeRemovedCampsiteRange: { startDate: string; campsiteId: string }
) => {
    const newSelection = cloneDeep(selection);

    const removeStartDate = toBeRemovedCampsiteRange.startDate;
    delete newSelection.dateRangeMap?.[removeStartDate];

    // If after deleting the sub-range, dateRangeMap becomes empty, assume selection no longer valid
    if (isEmpty(newSelection.dateRangeMap)) {
        return null;
    }

    // Update new startDate, endDate
    //@ts-ignore
    const newMinDate = reduce(
        newSelection.dateRangeMap,
        (current, endDate, startDate) => {
            return minDate(current, UTCStringToCalendarDate(startDate));
        },
        new CalendarDate(2050, 1, 1)
    );
    //@ts-ignore
    const newMaxDate = reduce(
        newSelection.dateRangeMap,
        (current, endDate) => {
            return maxDate(current, UTCStringToCalendarDate(endDate));
        },
        new CalendarDate(1920, 1, 1)
    );

    newSelection.startDate = calendarDateToUtcString(newMinDate) as string;
    newSelection.endDate = calendarDateToUtcString(newMaxDate) as string;
    newSelection.actualEndDate = calendarDateToUtcString(
        newMaxDate.subtract({ days: 1 })
    ) as string;

    return newSelection;
};

const updateExtendingSelection = ({
    selectedDate,
    matchedSelection,
    selections,
    availabilities,
    campgroundDateRange,
    isItinerarySingle,
    isInternal = false,
    canModifyAsItinerary,
}: {
    selectedDate: DateValue;
    matchedSelection: Selection;
    selections: Selections;
    availabilities: any;
    campgroundDateRange: any;
    isItinerarySingle: boolean;
    isInternal: boolean;
    canModifyAsItinerary: boolean;
}) => {
    /* If facility is ITINERARY_SINGLE and another campsite has selection between matchedSelection's end date and the new selection,
    create NEW date_range matchedSelection's range */
    if (
        isItinerarySingle &&
        hasOtherCampsiteInRange(selectedDate, matchedSelection, selections)
    ) {
        return extendWithNewSelection({
            selectedDate,
            matchedSelection,
            campgroundDateRange,
            isItinerarySingle,
        });
    }
    /* If facility is NOT ITINERARY_SINGLE or it is ITINERARY_SINGLE but there are NO selections between matchedSelection's
    end date and the new selection, EXTEND selection map entry in the matchedSelection */
    return extendExistingSelection({
        selectedDate,
        matchedSelection,
        availabilities,
        canHaveMultiSelectionsPerSite: isItinerarySingle || canModifyAsItinerary,
        isInternal,
    });
};

export const updateItinerarySelections = ({
    newDate,
    newCampsiteId,
    newCampsiteName,
    newCampsiteLoop,
    availabilitiesData,
    selections,
    campgroundDateRange,
    originalReservation,
    itineraryFlow = false,
    isInternal = false,
    canModifyAsItinerary,
}: {
    newDate: DateValue;
    newCampsiteId: string;
    newCampsiteName: string;
    newCampsiteLoop: string;
    availabilitiesData: any;
    selections: Selections;
    campgroundDateRange: any;
    originalReservation: any;
    itineraryFlow: boolean;
    isInternal: boolean;
    canModifyAsItinerary: boolean;
}) => {
    let shouldUpdate = false;
    let finalSelection = {};
    const { availabilities } = availabilitiesData;

    const matchedSelection = cloneDeep(selections[newCampsiteId]);
    const toBeRemoved = campsiteDateToRemove(matchedSelection, newDate);

    // Deletion
    if (toBeRemoved) {
        shouldUpdate = true;
    }
    // Maybe extend an already selected campsite's selection
    else if (selections[newCampsiteId]) {
        let isMiddleOfItinerary = false;

        // Check if single itinerary and the selected date is in the middle of
        // the selected itinerary. If so, we do nothing
        if (itineraryFlow && selections[newCampsiteId].dateRangeMap) {
            const dateRangeMap = selections[newCampsiteId].dateRangeMap;
            const dateRangeMapKeys = Object.keys(dateRangeMap);

            dateRangeMapKeys.forEach((key) => {
                const date1 = UTCStringToCalendarDate(key);
                const date2 = UTCStringToCalendarDate(dateRangeMap[key]).subtract({
                    days: 1,
                });

                if (newDate.compare(date1) >= 0 && newDate.compare(date2) <= 0) {
                    isMiddleOfItinerary = true;
                }
            });
        }

        // It's not in the middle so we can extend
        if (!isMiddleOfItinerary) {
            ({ finalSelection, shouldUpdate } = updateExtendingSelection({
                selectedDate: newDate,
                matchedSelection,
                selections,
                availabilities,
                campgroundDateRange,
                isItinerarySingle: itineraryFlow,
                isInternal,
                canModifyAsItinerary,
            }));
        }
    }
    // Create a brand new campsite selection
    else {
        ({ finalSelection, shouldUpdate } = updateSelectionsNew({
            selectedDate: newDate,
            selectedCampsiteId: newCampsiteId,
            isItinerarySingle: itineraryFlow,
            campgroundDateRange,
            availabilities,
            isInternal,
        }));
    }

    // == if selection is valid, return updated selections object ==
    if (shouldUpdate) {
        const updatedSelections = cloneDeep(selections);

        if (toBeRemoved) {
            if (matchedSelection.dateRangeMap) {
                //@ts-ignore
                const newSelection = handleRemoveSubRange(matchedSelection, toBeRemoved);
                let isTodaySameAsReservationStart;
                let originalStartDate;

                if (originalReservation) {
                    originalStartDate = UTCStringToCalendarDate(
                        originalReservation.start_date
                    );
                    isTodaySameAsReservationStart = isSameDay(
                        originalStartDate,
                        localToday
                    );
                }

                if (newSelection) {
                    updatedSelections[newCampsiteId] = newSelection;
                }
                // add extra row if it's the only selection
                else if (
                    originalReservation &&
                    canModifyAsItinerary &&
                    keys(updatedSelections).length === 1 &&
                    !isTodaySameAsReservationStart
                ) {
                    const today = localToday.add({ days: 1 });
                    updatedSelections[newCampsiteId] = {
                        campsiteId: newCampsiteId,
                        startDate: calendarDateToUtcString(originalStartDate) as string,
                        endDate: calendarDateToUtcString(today) as string,
                        actualEndDate: calendarDateToUtcString(localToday) as string,
                        dateRangeMap: {
                            [calendarDateToUtcString(originalStartDate) as string]:
                                calendarDateToUtcString(today) as string,
                        },
                    };
                    return updatedSelections;
                } else {
                    delete updatedSelections[newCampsiteId];
                }
            } else {
                delete updatedSelections[newCampsiteId];
            }
        } else {
            //@ts-ignore
            updatedSelections[newCampsiteId] = {
                ...finalSelection,
                name: newCampsiteName,
                loop: newCampsiteLoop,
            };
        }
        return updatedSelections;
    }

    return selections;
};

export const completeDateRangeMap = (selections: Selections) => {
    return reduce(
        selections,
        (acc: Selections, selection, campsiteId) => {
            let newSelection = selection;
            if (!selection.dateRangeMap) {
                newSelection = {
                    ...selection,
                    dateRangeMap: {
                        [selection.startDate]: selection.endDate,
                    },
                };
            }

            acc[campsiteId] = newSelection;
            return acc;
        },
        {}
    );
};

export const updateAvailabilitiesByUpdatedSelection = ({
    campground,
    updatedSelection,
    campsite,
    facilityType,
    canBookFCFS,
    earlyAccessLotteries,
    internalOriginalReservation,
}: {
    campground: Campground;
    updatedSelection: Selection;
    campsite: AvailabilityCampsite;
    facilityType: string;
    canBookFCFS: boolean;
    earlyAccessLotteries: any;
    internalOriginalReservation: Reservation;
}) => {
    const availabilitiesObj = cloneDeep(campsite);
    // If the campground has a running queuing lottery make sure to keep the processed availabilities for that before proceeding
    const hasQueuingLottery =
        isEqual(get(campground, 'facility_rules.hasQueueLottery.value', 0), 1) &&
        !isEmpty(get(campground, 'queue_lotteries', []));
    let processedAvailabilities = availabilitiesObj.availabilities;
    if (hasQueuingLottery) {
        const queueLotteries = get(campground, 'queue_lotteries', {});
        const activeLotteries = filterActiveEarlyAccessLotteries(
            queueLotteries,
            campground.facility_time_zone
        );

        if (!isEmpty(activeLotteries)) {
            processedAvailabilities = updateCampsiteAvailabilitiesByQueuingLottery({
                queueLotteries: activeLotteries,
                userEntries: earlyAccessLotteries,
                campsiteAvailabilities: availabilitiesObj.availabilities,
                reservation: internalOriginalReservation,
            });
        }
    }

    // if updated selection is undefined, that means the update is actually a removal. In this case, simply return the original availabilities
    if (!updatedSelection) {
        return processedAvailabilities;
    }

    const isDayUse = availabilitiesObj.type_of_use === TYPE_OF_USE_DAY;

    const lastDate = keys(processedAvailabilities).sort()[
        size(processedAvailabilities) - 1
    ];
    const extendedLastDate = formatAvailabilityAccessor(
        UTCStringToCalendarDate(lastDate).add({ days: 1 })
    );

    // STEP 1: Extend 1 day after the in the availabilities map as a potential checkout day
    const lastDayStatus = processedAvailabilities[lastDate];
    if (
        isStatusReservable(lastDayStatus) ||
        (includes(facilityType, ITINERARY_TYPE_FACILITY) &&
            lastDayStatus === CAMPSITE_UNAVAILABLE)
    ) {
        processedAvailabilities[extendedLastDate] =
            CampsiteAvailableTypes.CAMPSITE_NOT_AVAILABLE;
    }

    // STEP 2: Find a potential checkout-only date and adjust its availability status
    if (updatedSelection && !isEmpty(updatedSelection) && !isDayUse) {
        const checkoutOnlyDate = findNextAvailableCheckoutOnlyDate({
            startDate: UTCStringToCalendarDate(updatedSelection.startDate),
            availabilities: processedAvailabilities,
            canBookFCFS,
        });

        if (checkoutOnlyDate) {
            const prevDate = UTCStringToCalendarDate(checkoutOnlyDate).subtract({
                days: 1,
            });
            const prevDateAccessor = formatAvailabilityAccessor(prevDate);

            /* if prev date is potentially reservable in the ORIGINAL availabilities object,
            extend the 1 checkout-only date with status of "Checkout" */
            const prevDateStatus = processedAvailabilities[prevDateAccessor];
            if (isStatusReservable(prevDateStatus)) {
                set(processedAvailabilities, checkoutOnlyDate, CAMPSITE_CHECKOUT_ONLY);
            }
        }
    }

    return processedAvailabilities;
};

/** The account order reservation names the arrival `reservation_start`; the camping one `start_date`. */
type ArrivalDateSource = { reservation_start?: string; start_date?: string };

/**
 * True once the reservation's facility-local arrival day has begun. Decides which modification flow
 * applies - the reworked one cannot represent nights already spent - never whether the reservation
 * may be modified at all.
 */
export const hasReachedArrivalDate = (
    campground?: { facility_time_zone?: string } | null,
    reservation?: ArrivalDateSource | null
): boolean => {
    const reservationStart = reservation?.reservation_start || reservation?.start_date;
    if (!reservationStart) return false;

    return checkInFlightFacilityTimeReservationStartDate({
        reservationStart,
        facilityTimeZone: campground?.facility_time_zone ?? '',
    });
};

export const isInFlightModificationEnabled = (
    campground: Campground,
    reservation: Reservation,
    isInternal: boolean
) => {
    const isDayUse = reservation?.extra_info?.type_of_use === TYPE_OF_USE_DAY;
    const hasInFlightModificationRuleEnabled =
        campground?.facility_rules?.allowMidStayModification?.value === 1 || isInternal;

    return (
        hasInFlightModificationRuleEnabled &&
        hasReachedArrivalDate(campground, reservation) &&
        !isDayUse
    );
};

export const getUniqueCampsitesFromNightMap = (nightMap: NightMap) => {
    const campsiteSet: string[] = [];
    forEach(nightMap, (campsite) => {
        if (!includes(campsiteSet, campsite.campsite_id)) {
            campsiteSet.push(campsite.campsite_id);
        }
    });
    return campsiteSet;
};

export const getExtensionAvailability = ({
    availabilities,
    facilityType,
}: {
    availabilities: CampsiteAvailabilities;
    facilityType: string;
}) => {
    const processedAvailability = cloneDeep(availabilities);
    const lastDate = keys(processedAvailability).sort()[size(processedAvailability) - 1];
    const extendedLastDate = formatAvailabilityAccessor(
        UTCStringToCalendarDate(lastDate).add({ days: 1 })
    );

    /* If the last reservable date is 'Available', 'Open', or 'Not Reservable', we will extend 1 checkout-only date and
    mark it as "Not Available" (so that we can potentially extend a checkout-only date later)

    Itinerary's availabilities have a slightly different structure (selected sites are changed to 'Unavailable' instead
    of remaining 'Available'. Therefore if an itinerary grid's last reservable date is 'Unavailable', mark extended
    checkout date as 'Reserved' */
    const lastDayStatus = get(processedAvailability, lastDate, CAMPSITE_UNAVAILABLE);
    if (
        includes(RESERVABLE_AVAILABILITY_STATUSES, lastDayStatus) ||
        (includes(facilityType, ITINERARY_TYPE_FACILITY) &&
            lastDayStatus === CAMPSITE_UNAVAILABLE)
    ) {
        processedAvailability[extendedLastDate] =
            CampsiteAvailableTypes.CAMPSITE_NOT_AVAILABLE;
    }

    return processedAvailability;
};

/* Combine raw date range availability object from store to pure availabilities object with month(s) of
availability data combined into 1 'availabilities' entry */
export const combineAvailability = (campgroundAvailability = {}) => {
    const combinedDateRangeAvailability = {};
    forEach(campgroundAvailability, (value) => {
        merge(combinedDateRangeAvailability, value);
    });

    return combinedDateRangeAvailability;
};
